/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * DashboardCardStyles.js
 * Styles for DashboardCard components
 */

import { styled } from '@mui/material/styles';
import { Button, IconButton, Typography, Badge } from '@mui/material';
import CustomCard from '../../common/CustomCard';
import Icon from '../../icons/Icon';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';

const DASHBOARD_CARD_STUDY_PREP_TITLE_TYPOGRAPHY = CommonStyles().studyPrepTitle;

const DASHBOARD_CARD_TITLE_TYPOGRAPHY = {
  color: mixins.color_text_primary,
  fontFeatureSettings: '\'liga\' off',
  fontFamily: mixins.fontFamilyPlusSans,
  fontSize: mixins.fontSize24,
  fontStyle: 'normal',
  fontWeight: mixins.fontWeight500,
  lineHeight: mixins.lineHeight32,
  letterSpacing: '-0.48px'
};

export const DashboardCardRoot = styled(CustomCard)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    borderRadius: 24,
    '--title-max-width': '100%'
  }
});

export const DashboardCardTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'showCursorPointer'
})(({ showCursorPointer }) => ({
  '&&': {
    ...DASHBOARD_CARD_TITLE_TYPOGRAPHY,
    padding: '0px 8px',
    cursor: showCursorPointer ? 'pointer' : 'initial',
    ...(showCursorPointer ? { maxWidth: 'var(--title-max-width)' } : {})
  },
  '&&&.MuiTypography-body1': DASHBOARD_CARD_TITLE_TYPOGRAPHY
}));

export const DashboardCardStudyPrepTitle = styled(Typography)({
  '&&': DASHBOARD_CARD_STUDY_PREP_TITLE_TYPOGRAPHY,
  '&&&.MuiTypography-body1': DASHBOARD_CARD_STUDY_PREP_TITLE_TYPOGRAPHY
});

export const DashboardCardIconButton = styled(IconButton)({
  '&&': {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: '8px 16px 10px 16px',
    borderRadius: '1000px',
    minWidth: 'unset',

    '& .MuiSvgIcon-root': {
      width: '16px',
      height: '16px',
      fontSize: '16px'
    },

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  }
});

export const DashboardCardIconButtonMedium = styled(IconButton)({
  '&&': {
    color: mixins.navyColor,
    maxHeight: 40,
    maxWidth: 40,
    minWidth: 'unset',
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  }
});

export const DashboardCardIconMedium = styled(Icon)({
  '&&': {
    width: '44px',
    height: '44px',
    fontSize: '44px'
  }
});

export const DashboardCardHeader = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    minHeight: 40
  }
});

export const DashboardCardStudyPrepRoot = styled(DashboardCardRoot)({
  '&&': CommonStyles().studyPrepCard
});

export const DashboardCardStudyPrepHeader = styled(DashboardCardHeader)({
  '&&': CommonStyles().studyPrepCardHeader
});

export const DashboardCardActionsLeftContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    flexWrap: 'wrap',
    flexDirection: 'column',
    maxWidth: '90%',
    gap: 8,

    [mixins['md-sm-min']]: {
      width: 'auto',
      flexDirection: 'row'
    }
  }
});

export const DashboardCardActions = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isCompact' && prop !== 'alignLeft'
})(({ isCompact, alignLeft }) => ({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: isCompact ? 4 : 8,
    ...(alignLeft ? {
      justifyContent: 'flex-end',
      flexGrow: '1',

      [mixins['md-sm-min']]: {
        justifyContent: 'flex-start',
        flexGrow: '0'
      }
    } : {})
  }
}));

export const DashboardCardActionsRight = styled(DashboardCardActions)({
  '&&': {
    alignItems: 'flex-start',
    flexDirection: 'column',

    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    }
  }
});

export const DashboardCardContent = styled('div')({
  '&&': {}
});

export const DashboardCardIconOnlyButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'hasBadge'
})(({ hasBadge }) => ({
  '&&': {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: hasBadge ? 12 : 8,
    borderRadius: hasBadge ? '50%' : '1000px',
    minWidth: 'unset',
    ...(hasBadge ? {
      border: `1px solid ${mixins.secondaryButtonHoverColor}`,
      boxSizing: 'content-box'
    } : {}),

    '& .MuiSvgIcon-root': {
      width: '16px',
      height: '16px',
      fontSize: '16px'
    },

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  }
}));

export const DashboardCardBadge = styled(Badge)({
  '&&': {
    '& .MuiBadge-badge': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${mixins.badgeBgColoe}`,
      color: `${mixins.accentLightRed}`,
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: 12,
      padding: '0 4px 2px 4px',
      fontWeight: 600,
      textAlign: 'center',
      lineHeight: '20px',
      marginRight: '-5px'
    }
  }
});

export const DashboardCardSubTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    padding: '0 8px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight24,
    marginTop: -8
  },
  '&&&.MuiTypography-body1': {
    color: mixins.color_text_primary,
    padding: '0 8px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight24,
    marginTop: -8
  }
});

export const DashboardCardFooter = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center'
  }
});

export const DashboardCardButton = styled(Button)({
  '&&': {
    display: 'flex',
    padding: '8px 16px',
    justifyContent: 'center',
    alignItems: 'center',
    color: mixins.navyColor,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '22px',
    borderRadius: '1000px',
    minWidth: 'unset',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  }
});

export const DashboardCardTextIconButton = styled(Button)({
  '&&': {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: 8,
    borderRadius: '1000px',
    minWidth: 'unset',
    textTransform: 'none',
    background: 'transparent',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: 'none',

    '&.MuiButton-sizeMedium': {
      padding: 8,
      border: 'none'
    },

    '& > span:not(.MuiTouchRipple-root)': CommonStyles().buttonContentSpan,

    '& > span:not(.MuiTouchRipple-root) .MuiSvgIcon-root': {
      marginLeft: -4,
      width: '16px',
      height: '16px',
      fontSize: '16px'
    },

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  }
});
